/**
 * APPURTENANCES — the loads you hang on a tower: panel/sector antennas,
 * microwave dishes, omni/whips, RRU radios, and coax bundles.
 *
 * This module is the data model + a catalog of real, cited items + the pure
 * wind/weight load calculations. It reuses the TIA-222 velocity pressure from
 * `@/physics/wind` so the numbers line up with the rest of the sim.
 *
 * Units are SI throughout: metres, m² (area), kilograms (mass), Newtons
 * (force), Newton·metres (moment), Pascals (pressure), m/s (wind speed).
 *
 * Wind model (see docs/ENGINEERING.md §1 and docs/LOADING.md):
 *
 *   q(z) = 0.613 · Kz(z) · V²        velocity pressure at height z   (Pa)
 *   F    = q(z) · Gh · EPA           wind force on an appurtenance    (N)
 *
 * EPA (Effective Projected Area) is `Cf · A` — the drag coefficient is already
 * baked in by the manufacturer's datasheet, so we plug EPA straight into F and
 * do NOT apply a separate Cf. Gh is the gust effect factor (~0.85 lattice).
 */
import { velocityPressure } from "@/physics/wind";

/** Gust effect factor Gh, applied to the wind force (TIA-222, §1). [TYP 0.85] */
export const GUST_FACTOR = 0.85;

/** Standard gravity (m/s²) — for turning a dead weight into a downward force. */
export const G = 9.81;

export type AppurtenanceKind = "panel" | "dish" | "omni" | "rru" | "coax";

/**
 * A catalog item. `epa` is the FRONTAL effective projected area in m² (drag
 * coefficient already included, so wind force = q·Gh·epa). `weight` is the net
 * mass in kg. For `coax`, both `epa` and `weight` are PER METRE of run (m²/m and
 * kg/m respectively) — see `deadWeight`/`windForce`, which multiply by length.
 */
export interface Appurtenance {
  id: string;
  name: string;
  kind: AppurtenanceKind;
  /** Frontal EPA (m²); for `coax` this is m²/m (per metre of run). */
  epa: number;
  /** Net mass (kg); for `coax` this is kg/m (per metre of run). */
  weight: number;
  /** Dish reflector diameter (m) — dishes only. EPA scales ~D². */
  diameter?: number;
  /** Default mount standoff arm from the tower centreline (m). */
  standoff?: number;
  /** Short source note / datasheet the numbers came from. */
  source?: string;
}

/**
 * CATALOG — real appurtenances with cited EPA/weight. Numbers are from the
 * datasheets in `reference_towers/05_components/` and docs/ENGINEERING.md §1.5.
 * EPA is the datasheet FRONTAL value where published; where only a diameter is
 * given for a dish, EPA ≈ Cd · πD²/4 with Cd ≈ 1.3 (solid parabolic, §1.4).
 */
export const CATALOG = {
  // --- panel / sector antennas -------------------------------------------
  // CommScope NNHH-65A-R4: frontal EPA 0.48 m², net 31 kg (datasheet).
  panelSector: {
    id: "panel-sector",
    name: "Panel sector antenna (65°, ~2 m)",
    kind: "panel",
    epa: 0.68, // m² — representative modern sector panel (ENGINEERING.md §1.5 [STD])
    weight: 25, // kg
    standoff: 1.5,
    source: "ENGINEERING.md §1.5; cf. CommScope NNHH-65A-R4 (0.48 m² EPA, 31 kg)",
  },
  panelSmall: {
    id: "panel-small",
    name: "Small panel / metrocell",
    kind: "panel",
    epa: 0.3, // m² (0.2–0.4 range)
    weight: 10, // kg (5–15 range)
    standoff: 0.6,
    source: "ENGINEERING.md §1.5 [TYP]",
  },

  // --- microwave dishes (EPA scales ~D²) ---------------------------------
  // CommScope VHLP2 is a 0.6 m (2 ft) dish, ~8 kg net (datasheet).
  dish06: {
    id: "dish-0.6m",
    name: "Microwave dish 0.6 m",
    kind: "dish",
    epa: 0.37, // m² ≈ 1.3·π·0.6²/4
    weight: 10, // kg
    diameter: 0.6,
    standoff: 1.0,
    source: "ENGINEERING.md §1.5 (~0.3 m², ~10 kg); CommScope VHLP2 0.6 m ≈ 8 kg",
  },
  dish12: {
    id: "dish-1.2m",
    name: "Microwave dish 1.2 m",
    kind: "dish",
    epa: 1.47, // m² ≈ 1.3·π·1.2²/4
    weight: 35, // kg (30–40 range)
    diameter: 1.2,
    standoff: 1.5,
    source: "ENGINEERING.md §1.5 (~1.1–1.5 m², ~30–40 kg)",
  },
  dish18: {
    id: "dish-1.8m",
    name: "Microwave dish 1.8 m",
    kind: "dish",
    epa: 3.31, // m² ≈ 1.3·π·1.8²/4
    weight: 90, // kg (70–110 range)
    diameter: 1.8,
    standoff: 2.0,
    source: "ENGINEERING.md §1.5 (~2.5–3.4 m², ~70–110 kg)",
  },
  // CommScope HP8-59W: 2.4 m (8 ft) high-performance dish, 223 kg net;
  // datasheet axial wind force ~17.6 kN at 250 km/h survival. 3.0 m scales up.
  dish30: {
    id: "dish-3.0m",
    name: "Microwave dish 3.0 m",
    kind: "dish",
    epa: 9.19, // m² ≈ 1.3·π·3.0²/4
    weight: 300, // kg (300+)
    diameter: 3.0,
    standoff: 2.5,
    source: "ENGINEERING.md §1.5 (~7–9 m², ~300+ kg); cf. CommScope HP8-59W 2.4 m, 223 kg",
  },

  // --- omni / whip -------------------------------------------------------
  omni: {
    id: "omni-whip",
    name: "Omni / whip antenna",
    kind: "omni",
    epa: 0.1, // m² (0.05–0.15 range)
    weight: 6, // kg (2–10 range)
    standoff: 0.3,
    source: "ENGINEERING.md §1.5 [TYP]",
  },

  // --- RRU radio ---------------------------------------------------------
  // Ericsson Radio 4415: 20 kg (datasheet). EPA estimated from a ~0.4×0.3 m
  // face with Cd ≈ 1.4 (boxy sharp-edged unit) ≈ 0.17 m².
  rru: {
    id: "rru-radio",
    name: "RRU / remote radio unit",
    kind: "rru",
    epa: 0.17, // m²
    weight: 20, // kg
    standoff: 0.3,
    source: "Ericsson Radio 4415 datasheet (20 kg); EPA est. ~0.4×0.3 m face, Cd≈1.4",
  },

  // --- coax / feeder bundle (PER METRE) ----------------------------------
  // CommScope LDF5-50A is 7/8" heliax, ~28 mm over jacket, ~0.66 kg/m. A tower
  // typically runs a bundle of several. EPA/weight below are PER METRE of run.
  coaxBundle: {
    id: "coax-bundle",
    name: "Coax / feeder bundle (per metre)",
    kind: "coax",
    epa: 0.09, // m²/m — bundle ~0.09 m wide seen edge-on, Cd≈1.0 over 1 m
    weight: 4, // kg/m — ~6× 7/8" heliax @ ~0.66 kg/m
    standoff: 0.1,
    source: "CommScope LDF5-50A 7/8\" heliax (~0.66 kg/m, 28 mm OD), ×~6 bundle",
  },
} satisfies Record<string, Appurtenance>;

export type CatalogKey = keyof typeof CATALOG;

/**
 * A mounted load: an appurtenance placed on the tower at a height, optionally
 * with an azimuth (compass direction the antenna faces / the arm points) and a
 * standoff arm from the centreline. For `coax`, `runLength` (m) sets how much
 * feeder run this represents (defaults to the mount height — a run from base to
 * the mount).
 */
export interface MountedLoad {
  appurtenance: Appurtenance;
  /** Height above the tower base (m). Drives Kz and the moment arm F·h. */
  height: number;
  /** Optional azimuth the load faces / arm points, radians. Default 0 (+x). */
  azimuth?: number;
  /** Optional mount standoff arm from centreline (m). Defaults to the item's. */
  standoff?: number;
  /** Coax only: length of feeder run (m). Defaults to `height`. */
  runLength?: number;
}

/**
 * How many "units" of the appurtenance a mounted load represents. For coax this
 * is the run length in metres (so per-metre EPA/weight scale to the full run);
 * for everything else it is 1.
 */
export function loadUnits(load: MountedLoad): number {
  if (load.appurtenance.kind === "coax") {
    return load.runLength ?? load.height;
  }
  return 1;
}

/** Effective projected area contributed by one mounted load (m²). */
export function loadEPA(load: MountedLoad): number {
  return load.appurtenance.epa * loadUnits(load);
}

/** Dead weight (mass) of one mounted load (kg). */
export function deadWeight(load: MountedLoad): number {
  return load.appurtenance.weight * loadUnits(load);
}

/** Downward gravity force of one mounted load (N). */
export function deadWeightForce(load: MountedLoad): number {
  return deadWeight(load) * G;
}

/**
 * Wind force on one mounted load (N), height-aware via Kz. Uses the shared
 * velocity pressure q = 0.613·Kz(z)·V² and applies the gust factor and EPA:
 *
 *   F = q(height) · Gh · EPA
 *
 * `windSpeed` is the 3-second-gust speed in m/s. Returns the magnitude of the
 * horizontal force in the wind direction.
 */
export function windForce(load: MountedLoad, windSpeed: number): number {
  const q = velocityPressure(windSpeed, load.height);
  return q * GUST_FACTOR * loadEPA(load);
}

/**
 * Overturning moment contribution of one mounted load about the tower base
 * (N·m) = wind force × its height. This is the term that makes high, draggy
 * loads so punishing (docs/ENGINEERING.md §5).
 */
export function overturningMoment(load: MountedLoad, windSpeed: number): number {
  return windForce(load, windSpeed) * load.height;
}

// --- loadout aggregates -------------------------------------------------

/** Total effective projected area of a loadout (m²). */
export function totalEPA(loads: readonly MountedLoad[]): number {
  let sum = 0;
  for (const l of loads) sum += loadEPA(l);
  return sum;
}

/** Total added dead mass of a loadout (kg). */
export function totalDeadWeight(loads: readonly MountedLoad[]): number {
  let sum = 0;
  for (const l of loads) sum += deadWeight(l);
  return sum;
}

/** Total horizontal wind force of a loadout at a given wind speed (N). */
export function totalWindForce(loads: readonly MountedLoad[], windSpeed: number): number {
  let sum = 0;
  for (const l of loads) sum += windForce(l, windSpeed);
  return sum;
}

/**
 * Total overturning moment a loadout adds about the base (N·m) — Σ F·h. Compare
 * against the tower's resisting moment (W·d + footing) for the added-load factor
 * of safety (docs/ENGINEERING.md §5).
 */
export function totalOverturningMoment(loads: readonly MountedLoad[], windSpeed: number): number {
  let sum = 0;
  for (const l of loads) sum += overturningMoment(l, windSpeed);
  return sum;
}

/** A convenience bundle of everything a solver / readout wants for a loadout. */
export interface LoadoutSummary {
  count: number;
  epaM2: number;
  deadMassKg: number;
  windForceN: number;
  overturningMomentNm: number;
}

/** Summarise a loadout at a given wind speed (m/s). */
export function summariseLoadout(
  loads: readonly MountedLoad[],
  windSpeed: number,
): LoadoutSummary {
  return {
    count: loads.length,
    epaM2: totalEPA(loads),
    deadMassKg: totalDeadWeight(loads),
    windForceN: totalWindForce(loads, windSpeed),
    overturningMomentNm: totalOverturningMoment(loads, windSpeed),
  };
}
